import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import toast from 'react-hot-toast';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

// EditClaimModal — inline edit for the most commonly changed claim fields.
// Backend `PATCH /claims/:id` accepts a whitelist (see claimController
// updateClaim). Reserve changes push a reserveHistory entry server-side.
//
// This is intentionally a lightweight form rather than reusing the 4-step
// ClaimNew flow — most edits are single-field tweaks (bump reserve,
// change priority, fix a typo in the loss description).
function EditClaimModal({ claim, onClose, onSaved, S }) {
  const { t } = useTranslation();
  const [claimantName, setClaimantName] = useState('');
  const [priority, setPriority] = useState('medium');
  const [lossDate, setLossDate] = useState('');
  const [reserveAmount, setReserveAmount] = useState(0);
  const [lossDescription, setLossDescription] = useState('');
  const [slaDeadline, setSlaDeadline] = useState('');
  const [saving, setSaving] = useState(false);

  // Pre-fill inputs from the claim prop whenever it changes. Prior to the
  // 2026-07-04 fix these were set only in useState initializers, which run
  // once at mount — if the modal opened before the claim finished loading
  // (or if the parent kept the modal mounted across claim swaps), the
  // inputs stayed empty. S18 test caught this. Keying on claim._id makes
  // the sync explicit and side-effect-free on unrelated re-renders.
  useEffect(() => {
    if (!claim) return;
    setClaimantName(claim?.claimant?.name || '');
    setPriority(claim?.priority || 'medium');
    setLossDate(claim?.lossDate ? String(claim.lossDate).slice(0, 10) : '');
    setReserveAmount(claim?.reserveAmount ?? 0);
    setLossDescription(claim?.lossDescription || '');
    setSlaDeadline(claim?.slaDeadline ? String(claim.slaDeadline).slice(0, 10) : '');
  }, [claim?._id, claim?.claimant?.name, claim?.priority, claim?.lossDate, claim?.reserveAmount, claim?.lossDescription, claim?.slaDeadline]);

  const handleSave = async () => {
    if (!claim?._id) return;
    setSaving(true);
    try {
      const payload = {
        claimant: { ...claim.claimant, name: claimantName.trim() },
        priority,
        lossDate: lossDate || undefined,
        reserveAmount: Number(reserveAmount) || 0,
        lossDescription,
        slaDeadline: slaDeadline || undefined,
      };
      const resp = await axios.patch(`${BASE_URL}/claims/${claim._id}`, payload, { withCredentials: true });
      toast.success(t('screens.claimsScreen.detail.claimUpdated'));
      onSaved?.(resp.data?.data);
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.message || t('screens.claimsScreen.detail.saveClaimFailed'));
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '10px 12px', fontSize: 13, color: S.textPrimary,
    background: '#fff', border: `1px solid ${S.borderLight}`, borderRadius: 8,
    outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box',
  };
  const labelStyle = {
    display: 'block', fontSize: 11, fontWeight: 700, color: S.textMuted,
    textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 6,
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)',
        backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center',
        justifyContent: 'center', zIndex: 1000, padding: 20,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#fff', borderRadius: 16, padding: 28, width: '100%',
          maxWidth: 560, maxHeight: '90vh', overflowY: 'auto',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ fontSize: 17, fontWeight: 700, color: S.textPrimary, marginBottom: 6 }}>{t('screens.claimsScreen.detail.editClaimTitleHeader')}</div>
        <div style={{ fontSize: 12, color: S.textMuted, marginBottom: 24 }}>{claim?.claimNumber || '—'}</div>

        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>{t('screens.claimsScreen.detail.claimantName')}</label>
          <input value={claimantName} onChange={e => setClaimantName(e.target.value)} style={inputStyle} />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div>
            <label style={labelStyle}>{t('screens.claimsScreen.detail.priority')}</label>
            <select value={priority} onChange={e => setPriority(e.target.value)} style={inputStyle}>
              <option value='critical'>{t('screens.claimsScreen.priorityCritical')}</option>
              <option value='high'>{t('screens.claimsScreen.priorityHigh')}</option>
              <option value='medium'>{t('screens.claimsScreen.priorityMedium')}</option>
              <option value='low'>{t('screens.claimsScreen.priorityLow')}</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>{t('screens.claimsScreen.detail.lossDate')}</label>
            <input type='date' value={lossDate} onChange={e => setLossDate(e.target.value)} style={inputStyle} />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div>
            <label style={labelStyle}>{t('screens.claimsScreen.detail.reserveLabel')}</label>
            <input
              type='number' min='0' step='100'
              value={reserveAmount}
              onChange={e => setReserveAmount(e.target.value)}
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>{t('screens.claimsScreen.detail.slaDeadline')}</label>
            <input type='date' value={slaDeadline} onChange={e => setSlaDeadline(e.target.value)} style={inputStyle} />
          </div>
        </div>

        <div style={{ marginBottom: 24 }}>
          <label style={labelStyle}>{t('screens.claimsScreen.detail.lossDescription')}</label>
          <textarea
            value={lossDescription}
            onChange={e => setLossDescription(e.target.value)}
            style={{ ...inputStyle, minHeight: 90, resize: 'vertical' }}
          />
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            disabled={saving}
            style={{
              padding: '9px 18px', background: '#fff', color: S.textSecondary,
              border: `1px solid ${S.borderLight}`, borderRadius: 8,
              fontSize: 13, fontWeight: 600, cursor: 'pointer',
            }}
          >
            {t('screens.claimsScreen.detail.cancel')}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: '9px 20px', background: `linear-gradient(135deg, ${S.burgundy}, ${S.burgundy2})`,
              color: '#fff', border: 'none', borderRadius: 8,
              fontSize: 13, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.6 : 1,
            }}
          >
            {saving ? t('screens.claimsScreen.detail.saving') : t('screens.claimsScreen.detail.saveChanges')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default EditClaimModal;
